import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./lib/auth";
import { LangProvider } from "./lib/i18n";
import { has } from "./lib/api";
import Layout from "./components/app/Layout";
import Landing from "./pages/Landing";
import AuthCallback from "./pages/AuthCallback";
import Dashboard from "./pages/Dashboard";
import Realities from "./pages/Realities";
import ImportData from "./pages/ImportData";
import MasterList from "./pages/MasterList";
import Users from "./pages/Users";
import Audit from "./pages/Audit";
import Feedback from "./pages/Feedback";
import Privacy from "./pages/Privacy";
import Priorities from "./pages/Priorities";
import Plans from "./pages/Plans";
import PlanDetail from "./pages/PlanDetail";
import GkkProfiles from "./pages/GkkProfiles";
import GkkProfile from "./pages/GkkProfile";
import Evaluation from "./pages/Evaluation";
import Reports from "./pages/Reports";
import ProgramFeedback from "./pages/ProgramFeedback";
import Analyst from "./pages/Analyst";
import RateQR from "./pages/RateQR";
import PlanQR from "./pages/PlanQR";
import PpcDrafts from "./pages/PpcDrafts";
import PpcSummaries from "./pages/PpcSummaries";
import { ParishLink } from "./components/app/ParishLink";

const Protected = ({ perms, children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen grid place-items-center text-stone-500" data-testid="loading">Loading…</div>;
  if (!user) return <Navigate to="/" replace />;
  if (perms && !has(user, ...perms)) return <Navigate to="/dashboard" replace />;
  return <Layout>{children}</Layout>;
};

const QrGuard = () => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/" replace />;
  return <PlanQR />;
};

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/realities" element={<Protected perms={["analytics:all", "analytics:own_gkk"]}><Realities /></Protected>} />
      <Route path="/import" element={<Protected perms={["import:run"]}><ImportData /></Protected>} />
      <Route path="/master-list" element={<Protected perms={["gkk:manage"]}><MasterList /></Protected>} />
      <Route path="/users" element={<Protected perms={["users:manage"]}><Users /></Protected>} />
      <Route path="/audit" element={<Protected perms={["audit:view"]}><Audit /></Protected>} />
      <Route path="/feedback" element={<Protected><Feedback /></Protected>} />
      <Route path="/analyst" element={<Protected perms={["analyst:use"]}><Analyst /></Protected>} />
      <Route path="/analyst/drafts" element={<Protected perms={["analyst:use"]}><PpcDrafts /></Protected>} />
      <Route path="/ppc-summaries" element={<Protected perms={["ppc:read"]}><PpcSummaries /></Protected>} />
      <Route path="/rate/:token" element={<RateQR />} />
      <Route path="/plans/:id/qr" element={<QrGuard />} />
      <Route path="/evaluation" element={<Protected perms={["plan:propose"]}><Evaluation /></Protected>} />
      <Route path="/reports" element={<Protected perms={["report:generate", "report:own_gkk", "report:briefing"]}><Reports /></Protected>} />
      <Route path="/programs" element={<Protected><ProgramFeedback /></Protected>} />
      <Route path="/privacy" element={<Protected><Privacy /></Protected>} />
      <Route path="/priorities" element={<Protected perms={["plan:propose"]}><Priorities /></Protected>} />
      <Route path="/plans" element={<Protected perms={["plan:propose"]}><Plans /></Protected>} />
      <Route path="/plans/:id" element={<Protected perms={["plan:propose"]}><PlanDetail /></Protected>} />
      <Route path="/gkk-profiles" element={<Protected perms={["analytics:all", "analytics:own_gkk"]}><GkkProfiles /></Protected>} />
      <Route path="/gkk-profiles/:id" element={<Protected perms={["analytics:all", "analytics:own_gkk"]}><GkkProfile /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <LangProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRouter />
            <ParishLink />
          </BrowserRouter>
          <Toaster position="top-right" />
        </AuthProvider>
      </LangProvider>
    </div>
  );
}
